import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import Button from '../components/Button';
import { useProfile } from './ProfileContext';
import {
  listSaves, renameSave, deleteSave, touchSave, SAVE_LIMIT,
} from './accountApi';
import { fetchClubs } from '../game/gameApi';
import ClubCrest from '../game/ClubCrest';

const TIER_NAMES = { 1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two' };

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tx.muted, fontFamily: font.display, fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

const eyebrow = {
  fontFamily: font.display, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.24em', textTransform: 'uppercase', color: accent.lime,
};

const smallBtn = {
  fontFamily: font.display, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
  background: 'none', border: `1px solid ${line.strong}`, color: tx.secondary,
  borderRadius: radius.sm, padding: '7px 12px', transition: 'border-color .14s, color .14s',
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86400000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  return fmtDate(iso);
}

export default function Saves() {
  const navigate = useNavigate();
  const { profile, status: profileStatus } = useProfile();

  const [saves, setSaves] = useState([]);
  const [clubsById, setClubsById] = useState({});
  const [status, setStatus] = useState('loading');

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [s, c] = await Promise.all([listSaves(), fetchClubs()]);
      if (!alive) return;
      if (s.error) { setStatus('error'); return; }
      setSaves(s.data || []);
      const map = {};
      (c.data || []).forEach((club) => { map[club.id] = club; });
      setClubsById(map);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, []);

  const hasProfile = profileStatus === 'ready' && profile;
  const full = saves.length >= SAVE_LIMIT;

  function clubName(save) {
    const club = clubsById[save.club_id];
    return (club && club.name) || 'Unknown club';
  }
  function displayName(save) {
    return save.name || clubName(save);
  }

  async function onContinue(save) {
    touchSave(save.id);
    navigate(`/game/${save.id}`);
  }

  function startRename(save) {
    setConfirmId(null);
    setEditingId(save.id);
    setDraft(displayName(save));
  }

  async function commitRename(save) {
    const name = draft.trim() || clubName(save);
    setBusy(true);
    const { error } = await renameSave(save.id, name);
    setBusy(false);
    if (!error) {
      setSaves((prev) => prev.map((s) => (s.id === save.id ? { ...s, name } : s)));
    }
    setEditingId(null);
  }

  async function onDelete(save) {
    setBusy(true);
    const { error } = await deleteSave(save.id);
    setBusy(false);
    if (!error) {
      setSaves((prev) => prev.filter((s) => s.id !== save.id));
    }
    setConfirmId(null);
  }

  if (status === 'loading') return <Screen>Loading your saves</Screen>;
  if (status === 'error') return <Screen>Couldn&rsquo;t load your saves</Screen>;

  return (
    <div style={{ maxWidth: 900, padding: space.s6 }}>
      <div style={{ marginBottom: space.s6 }}>
        <div style={eyebrow}>Saves</div>
        <h1 style={{
          fontFamily: font.display, fontWeight: 700, margin: `${space.s2}px 0 0`,
          fontSize: 'clamp(30px, 4vw, 42px)', letterSpacing: '-0.01em',
        }}>Manage saves.</h1>
        <p style={{ color: tx.secondary, marginTop: space.s3, fontSize: 15, maxWidth: '54ch' }}>
          Each save is its own appointment. You can run up to {SAVE_LIMIT} at once. Free a slot to start another.
        </p>
        <div style={{
          marginTop: space.s4, display: 'inline-flex', alignItems: 'center', gap: space.s2,
          fontFamily: font.data, fontSize: 12.5, color: full ? accent.amber : tx.muted,
          border: `1px solid ${line.DEFAULT}`, borderRadius: radius.sm, padding: '6px 12px',
        }}>
          <span style={{ fontFamily: font.display, fontWeight: 700, color: tx.primary }}>{saves.length}</span>
          / {SAVE_LIMIT} slots used
        </div>
      </div>

      {saves.length === 0 ? (
        <div style={{
          background: bg.surface, border: `1px dashed ${line.strong}`, borderRadius: radius.lg,
          padding: space.s6, textAlign: 'center',
        }}>
          <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 18, color: tx.primary }}>
            No saves yet.
          </div>
          <p style={{ color: tx.muted, fontSize: 14, margin: `${space.s2}px 0 ${space.s4}px` }}>
            Take a club and start your first appointment.
          </p>
          {hasProfile
            ? <Button onClick={() => navigate('/hub/new')}>Start a new save</Button>
            : <Link to="/hub/profile" style={{ color: accent.lime, fontFamily: font.display, fontWeight: 600, textDecoration: 'none' }}>Create your profile first &rsaquo;</Link>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.s3 }}>
          {saves.map((save) => {
            const club = clubsById[save.club_id];
            const editing = editingId === save.id;
            const confirming = confirmId === save.id;
            return (
              <div key={save.id} style={{
                background: bg.surface, border: `1px solid ${line.DEFAULT}`, borderRadius: radius.lg,
                padding: space.s4, display: 'flex', alignItems: 'center', gap: space.s4, flexWrap: 'wrap',
              }}>
                <ClubCrest club={club} size={52} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  {editing ? (
                    <div style={{ display: 'flex', gap: space.s2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        autoFocus
                        value={draft}
                        maxLength={40}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename(save);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        style={{
                          fontFamily: font.display, fontWeight: 600, fontSize: 15, color: tx.primary,
                          background: bg.base, border: `1px solid ${accent.lime}`, borderRadius: radius.sm,
                          padding: '6px 10px', outline: 'none', minWidth: 160,
                        }}
                      />
                      <button type="button" disabled={busy} onClick={() => commitRename(save)}
                        style={{ ...smallBtn, borderColor: accent.lime, color: accent.lime }}>Save</button>
                      <button type="button" onClick={() => setEditingId(null)} style={smallBtn}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontFamily: font.display, fontWeight: 700, fontSize: 17, color: tx.primary, lineHeight: 1.15 }}>
                        {displayName(save)}
                      </div>
                      <div style={{ fontFamily: font.data, fontSize: 12, color: tx.muted, marginTop: 3 }}>
                        {club ? `${club.name} · ${TIER_NAMES[club.tier] || `Tier ${club.tier}`}` : 'Club unavailable'}
                        {' · '}last played {timeAgo(save.last_played_at)}
                      </div>
                    </>
                  )}
                </div>

                {!editing && (
                  confirming ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: space.s2, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: font.data, fontSize: 12.5, color: tx.secondary }}>
                        Delete this save? This can&rsquo;t be undone.
                      </span>
                      <button type="button" disabled={busy} onClick={() => onDelete(save)}
                        style={{ ...smallBtn, borderColor: accent.red, color: accent.red }}>Delete</button>
                      <button type="button" onClick={() => setConfirmId(null)} style={smallBtn}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: space.s2 }}>
                      <Button onClick={() => onContinue(save)} style={{ fontSize: 13, padding: '12px 18px' }}>Continue &rsaquo;</Button>
                      <button type="button" className="gw-save-btn" onClick={() => startRename(save)} style={smallBtn}>Rename</button>
                      <button type="button" className="gw-save-btn gw-save-btn--danger" onClick={() => { setEditingId(null); setConfirmId(save.id); }} style={smallBtn}>Delete</button>
                    </div>
                  )
                )}
              </div>
            );
          })}

          <div style={{ marginTop: space.s3 }}>
            {full ? (
              <div style={{
                fontFamily: font.data, fontSize: 13, color: tx.muted,
                border: `1px dashed ${line.strong}`, borderRadius: radius.lg, padding: space.s4, textAlign: 'center',
              }}>
                All {SAVE_LIMIT} save slots are in use. Delete one above to start another.
              </div>
            ) : hasProfile ? (
              <Button onClick={() => navigate('/hub/new')}>Start a new save</Button>
            ) : (
              <Link to="/hub/profile" style={{ color: accent.lime, fontFamily: font.display, fontWeight: 600, textDecoration: 'none' }}>
                Create your profile first &rsaquo;
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
